import { Injectable } from '@angular/core';
import { filter, map, Observable, share, startWith, switchMap } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { FragmentType } from '../common/typed-fragment/typed-fragment';
import { Message, MessageList } from '../models/user';
import { encodeMessageFilterParams } from '../models/params';
import { MessageAction, RealtimeToken } from '../models/messaging';
import { UserService } from './user.service';
import { URLService } from './dynamic-url.service';
import { RealtimeService } from './realtime.service';
import { GPTAppsService, GptAppConfig, GPTApp } from './space-apps.service';

export const PAGE_COUNT = 10;

@Injectable({
  providedIn: 'root',
})
export class ChatGptService {
  constructor(
    private userService: UserService,
    private http: HttpClient,
    private urlService: URLService,
    private realtimeService: RealtimeService,
    private appsService: GPTAppsService,
  ) {}

  askQuestion(data: { question: string; appId: string }): Observable<boolean> {
    const message: any = {
      content: [
        {
          data: data.question,
          type: FragmentType.Text,
        },
      ],
      context: {
        appId: data.appId,
      },
    };

    return this.userService.createComment(message as Message).pipe(map((response) => response.ok));
  }

  getAppConversation$(appId: string, page = 1, num = PAGE_COUNT): Observable<any> {
    const messageFilter = encodeMessageFilterParams({
      appId,
    });

    return this.http.get<MessageList>(
      `${this.urlService.getDynamicUrl()}/tutor/messages/search?filters=${messageFilter}&page=${page}&num=${
        num * page
      }`,
    );
  }

  getAppToken$(appId: string): Observable<RealtimeToken> {
    return this.getAppConversation$(appId, 1).pipe(
      map((data) => data['realtime']['realtime_user_message']),
    );
  }

  getUpdates$(appId: string): Observable<Message | null> {
    return this.getAppToken$(appId).pipe(
      switchMap((token) =>
        this.realtimeService.subscribeMessages$({ appId }, token).pipe(startWith(null)),
      ),
      share(),
    );
  }

  getMessageUpdates$(appId: string): Observable<Message | null> {
    return this.getUpdates$(appId).pipe(
      filter((message) =>
        Boolean(message === null || (message as any).messageAction === MessageAction.Add),
      ),
    );
  }

  getAppDetails$(appId: string, defaultConfig: GptAppConfig): Observable<GPTApp> {
    return this.getUpdates$(appId).pipe(
      filter((message) =>
        Boolean(message === null || (message as any).messageAction === MessageAction.AppUpdated),
      ),
      switchMap(() => this.appsService.getApp(appId)),
      startWith({
        _id: appId,
        bot: defaultConfig.botId,
        name: defaultConfig.name,
        title: 'Chat with GPT',
      } as GPTApp),
    );
  }

  unsubscribeFromUpdates(appId: string): void {
    this.realtimeService.unsubscribeFromChannelUsingContext({ appId });
  }
}
